import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ClipboardList, Users, Building2, Sparkles, FileBarChart2, LogOut, Bell, UserCircle2, Receipt, Settings as SettingsIcon, Menu, X, BookOpen, Trophy, Copy, Megaphone, TrendingUp, Clock3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { CLIENT_CATEGORIES } from "@/lib/clientCategories";
import StaffBell from "@/components/StaffBell";

const CLIENT_SUBNAV = [{ key: "", label: "All Clients" }, ...CLIENT_CATEGORIES];

const ADMIN_NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true, tid: "nav-dashboard", key: "dashboard" },
  { to: "/tasks", label: "Daily Tasks", icon: ClipboardList, tid: "nav-tasks", key: "tasks" },
  { to: "/invoices", label: "Invoices", icon: Receipt, tid: "nav-invoices", key: "invoices" },
  { to: "/ledger", label: "Khata / Ledger", icon: BookOpen, tid: "nav-ledger", key: "ledger" },
  { to: "/employees", label: "Employees", icon: Users, tid: "nav-employees", key: "employees" },
  { to: "/hr", label: "HR & Payroll", icon: Clock3, tid: "nav-hr", key: "hr" },
  { to: "/clients", label: "Clients", icon: Building2, tid: "nav-clients", key: "clients" },
  { to: "/duplicates", label: "Duplicates", icon: Copy, tid: "nav-duplicates", key: "clients" },
  { to: "/leads", label: "Leads & Follow-ups", icon: Sparkles, tid: "nav-leads", key: "leads" },
  { to: "/marketing", label: "Marketing & Sales", icon: TrendingUp, tid: "nav-marketing", key: "marketing" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, tid: "nav-reports", key: "reports" },
  { to: "/performance", label: "Performance", icon: Trophy, tid: "nav-performance", key: "reports" },
  { to: "/ads", label: "Advertisements", icon: Megaphone, tid: "nav-ads", key: "ads" },
  { to: "/ai-settings", label: "AI Settings", icon: Sparkles, tid: "nav-ai-settings", key: "settings" },
  { to: "/license", label: "License Control", icon: BookOpen, tid: "nav-license", key: "settings" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, tid: "nav-settings", key: "settings" },
];

const EMPLOYEE_NAV = [
  { to: "/", label: "My Overview", icon: LayoutDashboard, end: true, tid: "nav-dashboard", key: "dashboard" },
  { to: "/tasks", label: "My Tasks", icon: ClipboardList, tid: "nav-tasks", key: "tasks" },
  { to: "/duty", label: "My Duty", icon: Clock3, tid: "nav-duty", key: "duty" },
  { to: "/invoices", label: "Invoices", icon: Receipt, tid: "nav-invoices", key: "invoices" },
  { to: "/ledger", label: "Khata / Ledger", icon: BookOpen, tid: "nav-ledger", key: "ledger" },
  { to: "/clients", label: "Clients", icon: Building2, tid: "nav-clients", key: "clients" },
  { to: "/leads", label: "Leads & Follow-ups", icon: Sparkles, tid: "nav-leads", key: "leads" },
  { to: "/marketing", label: "Marketing & Sales", icon: TrendingUp, tid: "nav-marketing", key: "marketing" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, tid: "nav-reports", key: "reports" },
  { to: "/performance", label: "Performance", icon: Trophy, tid: "nav-performance", key: "reports" },
  { to: "/ads", label: "Advertisements", icon: Megaphone, tid: "nav-ads", key: "ads" },
  { to: "/profile", label: "My Profile", icon: UserCircle2, tid: "nav-profile", key: "profile" },
];

function OfficeStatus() {
  const now = new Date();
  const day = now.toLocaleDateString("en-IN", { weekday: "long" });
  const time = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const hour = now.getHours();
  const min = now.getMinutes();
  let phase = "Working hours";
  const inRange = (a, b, c, d) => (hour > a || (hour === a && min >= b)) && (hour < c || (hour === c && min < d));
  if (day === "Sunday" || hour < 9 || hour >= 18) phase = "Office closed";
  else if (inRange(13, 30, 14, 0)) phase = "Lunch break · resumes 2:00 PM";
  else if (inRange(16, 30, 16, 45)) phase = "Tea break · resumes 4:45 PM";
  return (
    <div className="text-[11px] tracking-[0.14em] uppercase text-zinc-500 flex items-center gap-3 font-medium">
      <span className="ticker-dot" />
      <span className="tabular">{day} · {time}</span>
      <span className="text-zinc-400">/</span>
      <span>{phase}</span>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [clientCounts, setClientCounts] = useState(null);
  const isAdmin = user?.role === "admin";
  const allowed = [...(user?.allowed_menus || ["dashboard", "tasks", "profile"]), "duty"];
  const BASE = isAdmin ? ADMIN_NAV : EMPLOYEE_NAV;
  const NAV = isAdmin ? BASE : BASE.filter(item => allowed.includes(item.key));
  const onClients = loc.pathname.startsWith("/clients");
  const activeCat = new URLSearchParams(loc.search).get("category") || "";

  React.useEffect(() => {
    if (!onClients) return;
    api.get("/clients/category-counts").then(r => setClientCounts(r.data)).catch(() => {});
  }, [onClients, loc.search]);

  // Close mobile drawer on route change + lock body scroll while open
  React.useEffect(() => { setMobileOpen(false); }, [loc.pathname, loc.search]);
  React.useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/reminders/send");
      toast.success(`Reminders sent`, { description: `${data.count} employee(s) notified` });
    } catch (e) {
      toast.error("Failed to send reminders", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 lg:grid lg:grid-cols-[260px_1fr]">
      {/* Mobile top bar — hamburger + brand */}
      <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 px-4 py-3 ios-header flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-zinc-900 text-white grid place-items-center font-heading font-black text-sm">M</div>
          <div>
            <div className="font-heading font-bold text-[13px] tracking-tight leading-none">Mind Maker 369</div>
            <div className="text-[9px] tracking-[0.18em] uppercase text-zinc-500 mt-0.5">Tax Ops Console</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
        <StaffBell variant="mobile" />
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          className="p-2 hover:bg-zinc-100 rounded"
          aria-label="Open menu"
        >
          <Menu size={22} strokeWidth={1.75} />
        </button>
        </div>
      </div>

      {/* Backdrop */}
      {mobileOpen ? (
        <div
          data-testid="mobile-drawer-backdrop"
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      {/* Sidebar — drawer on mobile, static on lg+ */}
      <aside
        data-testid="app-sidebar"
        className={`bg-white border-r border-zinc-200 flex flex-col
          fixed inset-y-0 left-0 z-50 w-[280px] transform transition-transform duration-200
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen lg:w-auto lg:z-0 overflow-hidden ios-drawer`}
      >
        <div className="px-6 py-6 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-zinc-900 text-white grid place-items-center font-heading font-black text-lg">M</div>
            <div>
              <div className="font-heading font-bold text-[15px] tracking-tight leading-none">Mind Maker 369</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">Tax Ops Console</div>
            </div>
          </div>
          <button
            data-testid="mobile-drawer-close"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1 hover:bg-zinc-100 rounded"
            aria-label="Close menu"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <nav className="flex-1 min-h-0 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, end, tid, key }) => (
            <React.Fragment key={to}>
              <NavLink
                to={to}
                end={end}
                data-testid={tid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                    isActive ? "side-active" : "text-zinc-700 hover:bg-zinc-100"
                  }`
                }
              >
                <Icon size={17} strokeWidth={1.75} />
                <span>{label}</span>
              </NavLink>
              {key === "clients" && onClients ? (
                <div className="ml-6 pl-3 border-l border-zinc-200 my-1 space-y-0.5" data-testid="clients-submenu">
                  {CLIENT_SUBNAV.map(s => {
                    const active = activeCat === s.key;
                    const count = clientCounts ? (s.key ? clientCounts[s.key] : clientCounts.all) : null;
                    return (
                      <NavLink
                        key={s.key || "all"}
                        to={s.key ? `/clients?category=${s.key}` : "/clients"}
                        data-testid={`subnav-clients-${s.key || "all"}`}
                        className={`flex items-center justify-between px-2.5 py-1.5 rounded text-[13px] transition-colors ${active ? "bg-zinc-100 text-zinc-900 font-semibold" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"}`}
                      >
                        <span>{s.label}</span>
                        {count != null ? <span className="tabular text-[10px] text-zinc-500">{count}</span> : null}
                      </NavLink>
                    );
                  })}
                </div>
              ) : null}
            </React.Fragment>
          ))}
        </nav>

        <div className="p-3 border-t border-zinc-200">
          {isAdmin ? (
            <button
              data-testid="send-reminders-btn"
              onClick={sendReminders}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors font-medium"
            >
              <Bell size={16} strokeWidth={1.75} /> Send 24h reminders
            </button>
          ) : null}
        </div>

        <div className="px-3 pb-4">
          <div className="p-3 border border-zinc-200 rounded-md flex items-center gap-3 bg-zinc-50">
            <div className="w-8 h-8 rounded-full bg-zinc-900 text-white grid place-items-center text-[11px] font-heading font-bold">
              {(user?.name || "?").split(" ").map(w => w[0]).slice(0,2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium truncate" data-testid="current-user-name">{user?.name || "Admin"}</div>
              <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 truncate">{user?.role || "admin"}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); nav("/login"); }}
              title="Sign out"
              className="p-2 hover:bg-zinc-200 rounded transition-colors"
            >
              <LogOut size={15} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex flex-col min-w-0">
        <header className="hidden lg:flex bg-white border-b border-zinc-200 px-8 py-4 items-center justify-between">
          <OfficeStatus />
          <div className="flex items-center gap-4">
            <StaffBell />
            <div className="text-[11px] tracking-[0.14em] uppercase text-zinc-500 truncate max-w-[280px]">
              {user?.email}
            </div>
          </div>
        </header>
        <div className="flex-1 p-4 sm:p-6 lg:p-8 max-w-full lg:max-w-[1400px] w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
